import { usePersistedState } from "@/lib/persistedFilters";
// Statements tab — line-level browse with filters, totals and per-line overrides.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, Pencil, CheckCircle2, Clock, Ban, Lock, Download } from "lucide-react";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { money, pct, apiError, thCls, tdCls, numCls } from "./engineShared";
import { NewPill } from "./NewPill";

export function EngineStatements({ overview, canManage, initialPeriod }) {
  const months = overview?.months || [];
  const [period, setPeriod] = useState(initialPeriod || months[months.length - 1] || "");
  const [q, setQ] = usePersistedState("engine-statements.q", "");
  const [paid, setPaid] = useState("all");
  const [excluded, setExcluded] = useState("0");
  const [hideZero, setHideZero] = useState(false);
  const [overridesOnly, setOverridesOnly] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, totals: {} });
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState(null);

  useEffect(() => { if (!period && months.length) setPeriod(months[months.length - 1]); }, [months.length]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/commissions/engine/statements", { params: {
        period: period || undefined, q: q || undefined, page, page_size: 100,
        paid: paid === "all" ? undefined : paid, excluded: excluded === "all" ? undefined : excluded,
        hide_zero: hideZero ? 1 : undefined, overrides: overridesOnly ? 1 : undefined,
        new: newOnly && period ? 1 : undefined,
      } });
      setData(d);
    } catch (e) { apiError(e, "Could not load statements"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period, paid, excluded, hideZero, overridesOnly, newOnly, page]);
  const pages = Math.max(1, Math.ceil((data.total || 0) / 100));
  const exportQS = new URLSearchParams({
    ...(period ? { period } : {}), ...(paid !== "all" ? { paid } : {}),
    ...(excluded !== "all" ? { excluded } : {}), ...(q.trim() ? { q: q.trim() } : {}),
    ...(hideZero ? { hide_zero: "1" } : {}), ...(overridesOnly ? { overrides: "1" } : {}),
    ...(newOnly && period ? { new: "1" } : {}),
  }).toString();

  return (
    <div className="space-y-3" data-testid="engine-statements">
      <Card className="border-slate-200 p-3 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-1 flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs text-slate-500">Month</Label>
              <Select value={period || "none"} onValueChange={(v) => { setPeriod(v === "none" ? "" : v); setPage(1); }}>
                <SelectTrigger className="h-9 w-36" data-testid="engine-st-period"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {months.length === 0 && <SelectItem value="none">No data</SelectItem>}
                  {[...months].reverse().map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Invoice</Label>
              <Select value={paid} onValueChange={(v) => { setPaid(v); setPage(1); }}>
                <SelectTrigger className="h-9 w-32" data-testid="engine-st-paid"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Any</SelectItem><SelectItem value="1">Paid</SelectItem><SelectItem value="0">Unpaid</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Lines</Label>
              <Select value={excluded} onValueChange={(v) => { setExcluded(v); setPage(1); }}>
                <SelectTrigger className="h-9 w-40" data-testid="engine-st-excluded"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="0">Commissionable</SelectItem><SelectItem value="1">Excluded / no rate</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="relative flex-1 min-w-[220px]">
              <Label className="text-xs text-slate-500">Search</Label>
              <Search className="absolute left-2 top-[30px] h-4 w-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
                     placeholder="domain, customer, agent, product, number, invoice #" className="h-9 pl-8" data-testid="engine-st-search" />
            </div>
            <label className="flex h-9 items-center gap-2 whitespace-nowrap text-xs text-slate-600" title="Hide lines with $0 commission">
              <Switch checked={hideZero} onCheckedChange={(v) => { setHideZero(v); setPage(1); }} data-testid="engine-st-hidezero" /> Hide $0
            </label>
            <label className="flex h-9 items-center gap-2 whitespace-nowrap text-xs text-slate-600" title="Show only lines with a manual override">
              <Switch checked={overridesOnly} onCheckedChange={(v) => { setOverridesOnly(v); setPage(1); }} data-testid="engine-st-overrides" /> Overrides only
            </label>
            <label className={`flex h-9 items-center gap-2 whitespace-nowrap text-xs ${period ? "text-blue-700" : "text-slate-400"}`}
                   title={period ? "Show only items billed for the first time this month" : "Pick a month first"}>
              <Switch checked={newOnly && !!period} disabled={!period}
                      onCheckedChange={(v) => { setNewOnly(v); setPage(1); }} data-testid="engine-st-newonly" /> New only
            </label>
            <Button variant="outline" className="h-9" onClick={() => { setPage(1); load(); }} data-testid="engine-st-apply">Apply</Button>
            <a href={`${API}/commissions/engine/statements/export.csv?${exportQS}`}
               className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm hover:bg-slate-50" data-testid="engine-st-export">
              <Download className="h-4 w-4" /> Export CSV
            </a>
          </div>
          <div className="ml-auto min-w-[200px] divide-y divide-slate-200 rounded-md border border-slate-200 bg-slate-50 text-xs tabular-nums" data-testid="engine-st-totals">
            <div className="px-3 py-1 text-[11px] text-slate-400">{(data.total || 0).toLocaleString()} lines</div>
            {data.new_count !== null && data.new_count !== undefined && (
              <button type="button" onClick={() => { setNewOnly((v) => !v); setPage(1); }}
                      className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-blue-50"
                      title={newOnly ? "Show every line again" : "Show only the new items"} data-testid="engine-st-new-total">
                <span className="text-blue-700">New items</span>
                <b className="rounded-full bg-blue-100 px-1.5 text-blue-700 ring-1 ring-inset ring-blue-200" data-testid="engine-st-new-count">{data.new_count.toLocaleString()}</b>
              </button>
            )}
            <div className="flex items-center justify-between px-3 py-1.5"><span className="text-slate-500">Billed</span><b className="text-slate-800">{money(data.totals?.billed)}</b></div>
            <div className="flex items-center justify-between px-3 py-1.5"><span className="text-slate-500">Commission</span><b className="text-slate-800">{money(data.totals?.commission)}</b></div>
            <div className="flex items-center justify-between px-3 py-1.5"><span className="text-amber-700">Unpaid invoices</span><b className="text-amber-700" data-testid="engine-st-unpaid">{money(data.totals?.unpaid_commission)}</b></div>
            <div className="flex items-center justify-between px-3 py-1.5"><span className="text-emerald-700">Paid invoices</span><b className="text-emerald-700">{money(data.totals?.paid_commission)}</b></div>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="engine-st-table">
            <thead className="bg-slate-50"><tr>
              <th className={thCls}>Date</th><th className={thCls}>Agent</th><th className={thCls}>Domain / customer</th>
              <th className={thCls}>Product · item</th><th className={`${thCls} text-right`}>Qty</th><th className={`${thCls} text-right`}>Billed</th>
              <th className={`${thCls} text-right`}>Rate</th><th className={`${thCls} text-right`}>Commission</th><th className={thCls}>Invoice</th><th className={thCls}></th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400" data-testid="engine-st-empty">No statement lines match</td></tr>}
              {!loading && data.items.map((ln) => {
                const ov = ln.override && Object.keys(ln.override).length > 0;
                return (
                  <tr key={ln.line_id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${ln.excluded ? "opacity-60" : ""}`} data-testid={`engine-st-row-${ln.line_id}`}>
                    <td className={`${tdCls} whitespace-nowrap text-slate-600`}>{ln.date || ln.period_label}</td>
                    <td className={tdCls}><div className="font-medium text-slate-800">{ln.agent || "—"}</div><div className="text-xs text-slate-500">{ln.territory} · {ln.level_name || "—"}</div></td>
                    <td className={tdCls}><div className="flex items-center gap-1.5 text-slate-800">{ln.domain || "—"}{ln.is_new_deal && <NewPill kind="deal" testId={`engine-st-newdeal-${ln.line_id}`} />}</div><div className="text-xs text-slate-500 truncate max-w-[220px]">{ln.customer_name}</div></td>
                    <td className={tdCls}><div className="text-slate-800">{ln.product_name}{ln.is_purchase && <span className="ml-1 text-[10px] text-violet-700">purchase</span>}{ln.is_prorate && <span className="ml-1 text-[10px] text-amber-700">prorated</span>}{ln.is_new && <NewPill first={ln.first_period} className="ml-1.5" testId={`engine-st-new-${ln.line_id}`} />}</div><div className="text-xs text-slate-500 truncate max-w-[220px]">{ln.tracked_item}</div></td>
                    <td className={numCls}>{ln.quantity}</td>
                    <td className={numCls}>{money(ln.billed_amount)}{ln.discount_pct ? <div className="text-[10px] text-slate-400">-{ln.discount_pct}% disc.</div> : null}</td>
                    <td className={numCls}>{pct(ln.rate)}<div className="text-[10px] text-slate-400">{ln.own_domain ? "own domain" : ln.rate_type}</div></td>
                    <td className={`${numCls} font-semibold ${ln.excluded ? "line-through text-slate-400" : ln.invoice_paid ? "text-emerald-700" : "text-amber-700"}`}>{money(ln.commission)}{ov && <div className="text-[10px] text-violet-700">override</div>}{ln.excluded && <div className="text-[10px] text-slate-500">{ln.exclude_reason === "no_rate" ? "no rate" : "excluded"}</div>}</td>
                    <td className={`${tdCls} whitespace-nowrap text-xs`}>
                      <div className="text-slate-700">#{ln.invoice_number}</div>
                      {ln.invoice_paid ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3 w-3" />paid {ln.invoice_payment_date || ""}</span>
                                       : <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="h-3 w-3" />unpaid</span>}
                    </td>
                    <td className={tdCls}>{canManage && <Button variant="ghost" size="sm" onClick={() => setEdit(ln)} data-testid={`engine-st-edit-${ln.line_id}`}><Pencil className="h-3.5 w-3.5" /></Button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 text-xs text-slate-600">
          <span>Page {page} of {pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="engine-st-prev">Prev</Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="engine-st-next">Next</Button>
          </div>
        </div>
      </Card>

      {edit && <LineOverrideDialog line={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function LineOverrideDialog({ line, onClose, onSaved }) {
  const ov = line.override || {};
  const [mode, setMode] = useState(ov.commission !== undefined ? "commission" : ov.rate !== undefined ? "rate" : ov.excluded ? "exclude" : "rate");
  const [rate, setRate] = useState(ov.rate ?? line.rate ?? "");
  const [commission, setCommission] = useState(ov.commission ?? line.commission ?? "");
  const [note, setNote] = useState(ov.note || "");
  const [saving, setSaving] = useState(false);

  const save = async (clear = false) => {
    setSaving(true);
    try {
      const body = clear ? { clear: true } : {
        note, excluded: mode === "exclude" ? true : false,
        rate: mode === "rate" ? Number(rate) : undefined,
        commission: mode === "commission" ? Number(commission) : undefined,
      };
      if (!clear && mode !== "exclude" && !note.trim()) { toast.error("Add a note explaining the exception"); setSaving(false); return; }
      await api.patch(`/commissions/engine/statements/${line.line_id}`, body);
      toast.success(clear ? "Override cleared — month rolled up again" : "Exception saved — month rolled up again");
      onSaved();
    } catch (e) { apiError(e, "Could not save"); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="engine-line-dialog">
        <DialogHeader>
          <DialogTitle>One-time exception</DialogTitle>
          <DialogDescription>{line.domain} · {line.product_name} {line.tracked_item && `· ${line.tracked_item}`} · invoice #{line.invoice_number} ({line.period_label})</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="rounded-md bg-slate-50 p-2"><div className="text-[11px] text-slate-500">Billed</div><div className="font-medium">{money(line.billed_amount)}</div></div>
          <div className="rounded-md bg-slate-50 p-2"><div className="text-[11px] text-slate-500">Engine rate</div><div className="font-medium">{pct(line.rate_calc ?? line.rate)}</div></div>
          <div className="rounded-md bg-slate-50 p-2"><div className="text-[11px] text-slate-500">Engine commission</div><div className="font-medium">{money(line.commission_calc)}</div></div>
        </div>
        {line.locked && <div className="flex items-center gap-1 text-xs text-slate-500"><Lock className="h-3 w-3" /> Invoice is paid — an override here is the only way to change this line.</div>}
        <div className="space-y-3">
          <div>
            <Label>Exception type</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="h-9" data-testid="engine-line-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rate">Fixed rate %</SelectItem>
                <SelectItem value="commission">Fixed commission $</SelectItem>
                <SelectItem value="exclude">Exclude this line</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {mode === "rate" && <div><Label>Rate %</Label><Input type="number" step="0.01" min="0" max="100" value={rate} onChange={(e) => setRate(e.target.value)} className="h-9" data-testid="engine-line-rate" /></div>}
          {mode === "commission" && <div><Label>Commission $</Label><Input type="number" step="0.01" value={commission} onChange={(e) => setCommission(e.target.value)} className="h-9" data-testid="engine-line-commission" /></div>}
          <div><Label>Note</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Why this exception applies" data-testid="engine-line-note" /></div>
        </div>
        <DialogFooter className="flex-wrap gap-2">
          {Object.keys(ov).length > 0 && <Button variant="outline" onClick={() => save(true)} disabled={saving} className="mr-auto text-rose-600" data-testid="engine-line-clear"><Ban className="h-4 w-4" /> Clear override</Button>}
          <Button variant="outline" onClick={onClose} data-testid="engine-line-cancel">Cancel</Button>
          <Button onClick={() => save(false)} disabled={saving} data-testid="engine-line-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save exception</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
